import classNames from "classnames/bind";
import { nanoid } from "nanoid";
import React, { useRef, useState } from "react";
import { colours } from "../../../constants/colours";
import { Icon, IconLoading } from "../../../Icon";
import { Skeleton } from "../../../Skeleton";
import { Tooltip } from "../../../Tooltip";
import styles from "./DataInputWithAdornment.module.scss";
import type { DataInputWithAdornmentProps } from "./types";

const cx = classNames.bind(styles);

const DataInputWithAdornment = ({
  children,
  adornmentSlot,
  showStatus = true,
  statusIconSlot,
  statusMessageSlot,
  detailsSlot,
  error,
  isDisabled = false,
  isBusy = false,
  isLoading = false,
  className,
  style,
}: DataInputWithAdornmentProps) => {
  const detailsAreaRef = useRef(nanoid());
  const [showDetails, setShowDetails] = useState(false);

  const getDefaultStatusIcon = () => {
    if (error) {
      return (
        <Icon
          variant="warning-circle"
          colour={isDisabled ? colours.surface[100] : colours.status.error}
          size={16}
          alt="Error"
        />
      );
    } else if (isBusy) {
      return (
        <IconLoading
          speed="fast"
          size={16}
          {...(isDisabled ? { colour: colours.surface[100] } : {})}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isBusy}
    >
      <div
        className={cx(
          "container",
          !!error && "error",
          isDisabled && "disabled",
          className
        )}
        style={style}
      >
        <div className={cx("display")}>
          {children({ isLoading, isBusy, error, isDisabled })}
        </div>
        <div className={cx("adornment", isDisabled && "disabled")}>
          <div className={cx("type")}>
            {adornmentSlot({
              iconProps: {
                size: 16,
              },
              showDetails,
              setShowDetails,
              ariaControls: detailsAreaRef.current,
              caretIcon: detailsSlot ? (
                <Icon
                  variant={showDetails ? "caret-up" : "caret-down"}
                  size={14}
                  weight="bold"
                />
              ) : null,
              TriggerWrapper: ({ children, className, ...rest }) => (
                <button
                  {...rest}
                  onClick={() => setShowDetails((prev) => !prev)}
                  aria-controls={detailsAreaRef.current}
                  className={cx("details-trigger", className)}
                  type="button"
                >
                  {children}
                </button>
              ),
            })}
          </div>
          {showStatus ? (
            <>
              <p className={cx("status", isDisabled && "disabled")}>
                <span className={cx("status-message")}>
                  {statusMessageSlot?.({
                    isLoading,
                    isBusy,
                    error,
                  })}
                </span>
                <span className={cx("status-icon--without-tooltip")}>
                  {statusIconSlot?.({
                    isLoading,
                    isBusy,
                    error,
                    iconProps: {
                      size: 16,
                      ...(isDisabled ? { colour: colours.surface[100] } : {}),
                    },
                  }) ?? getDefaultStatusIcon()}
                </span>
              </p>
              <p className={cx("status--with-tooltip")}>
                <Tooltip
                  content={statusMessageSlot?.({
                    isLoading,
                    isBusy,
                    error,
                  })}
                  position={["bottom", "right", "left", "top"]}
                  contentClassName={cx("status-icon")}
                >
                  {statusIconSlot?.({
                    isLoading,
                    isBusy,
                    error,
                    iconProps: {
                      size: 16,
                      ...(isDisabled ? { colour: colours.surface[100] } : {}),
                    },
                  }) ?? getDefaultStatusIcon()}
                </Tooltip>
              </p>
            </>
          ) : null}
        </div>
        {detailsSlot ? (
          <>
            <div
              className={cx("details-display-area", isDisabled && "disabled")}
              style={{ display: showDetails ? "block" : "none" }}
              id={detailsAreaRef.current}
            >
              {detailsSlot}
            </div>
          </>
        ) : null}
      </div>
    </Skeleton>
  );
};

export default DataInputWithAdornment;
